'use client';

/**
 * ReferredClientsFilters — the calm replacement for the old cycle-button.
 *
 * Status filter chips (with live counts) + an explicit Sort dropdown + an
 * asc/desc direction toggle. Fully predictable: the user can see every option
 * and the current state at a glance — no more "click and it shuffles randomly".
 *
 * Controlled component: all state lives in the parent (ReferredClientsTable),
 * filtering/sorting happens client-side on the already-fetched rows.
 */

import React from 'react';
import { ArrowDownNarrowWide, ArrowUpNarrowWide } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import type { ReferredClient } from '../types';

export type StatusFilter = ReferredClient['status'] | 'all';
export type SortField = 'status' | 'clientName' | 'planLevel' | 'commission' | 'totalEarned';
export type SortOrder = 'asc' | 'desc';

/** Chip order + labels. Only chips with count > 0 (plus "All") are rendered. */
const STATUS_CHIPS: { key: StatusFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'active', label: 'Active' },
  { key: 'payment_pending', label: 'Trial' },
  { key: 'past_due', label: 'Past Due' },
  { key: 'canceled', label: 'Canceled' },
  { key: 'inactive', label: 'Inactive' },
];

const SORT_OPTIONS: { value: SortField; label: string }[] = [
  { value: 'status', label: 'Status' },
  { value: 'clientName', label: 'Name' },
  { value: 'planLevel', label: 'Plan' },
  { value: 'commission', label: 'Monthly' },
  { value: 'totalEarned', label: 'Total Earned' },
];

interface ReferredClientsFiltersProps {
  statusFilter: StatusFilter;
  onStatusFilterChange: (status: StatusFilter) => void;
  sortField: SortField;
  onSortFieldChange: (field: SortField) => void;
  sortOrder: SortOrder;
  onSortOrderChange: (order: SortOrder) => void;
  /** Count per status across the (period-scoped) dataset, plus `all`. */
  counts: Record<StatusFilter, number>;
}

export function ReferredClientsFilters({
  statusFilter,
  onStatusFilterChange,
  sortField,
  onSortFieldChange,
  sortOrder,
  onSortOrderChange,
  counts,
}: ReferredClientsFiltersProps) {
  const visibleChips = STATUS_CHIPS.filter((c) => c.key === 'all' || (counts[c.key] ?? 0) > 0);

  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      {/* Status filter chips */}
      <div role="group" aria-label="Filter by status" className="flex flex-wrap items-center gap-1.5">
        {visibleChips.map(({ key, label }) => {
          const isActive = statusFilter === key;
          const count = counts[key] ?? 0;
          return (
            <button
              key={key}
              type="button"
              onClick={() => onStatusFilterChange(key)}
              aria-pressed={isActive}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-[11px] font-semibold transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/50',
                isActive
                  ? 'bg-amber-500/15 text-amber-500'
                  : 'text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800/60'
              )}
            >
              {label}
              <span
                className={cn(
                  'tabular-nums rounded-md px-1.5 py-0.5 text-[10px]',
                  isActive
                    ? 'bg-amber-500/20 text-amber-500'
                    : 'bg-zinc-200/70 dark:bg-zinc-800 text-zinc-500'
                )}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Sort field + direction */}
      <div className="flex items-center gap-1.5">
        <Select value={sortField} onValueChange={(v) => onSortFieldChange(v as SortField)}>
          <SelectTrigger
            aria-label="Sort by"
            className="h-8 w-[140px] text-xs rounded-lg bg-transparent border-zinc-200 dark:border-zinc-800 text-zinc-500"
          >
            <span className="text-zinc-400 mr-1">Sort:</span>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SORT_OPTIONS.map(({ value, label }) => (
              <SelectItem key={value} value={value} className="text-xs">
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <button
          type="button"
          onClick={() => onSortOrderChange(sortOrder === 'asc' ? 'desc' : 'asc')}
          aria-label={`Sort ${sortOrder === 'asc' ? 'ascending' : 'descending'} — click to reverse`}
          className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-zinc-200 dark:border-zinc-800 text-zinc-500 transition-colors hover:bg-zinc-100 dark:hover:bg-zinc-800 hover:text-zinc-700 dark:hover:text-zinc-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/50"
        >
          {sortOrder === 'asc' ? (
            <ArrowUpNarrowWide className="h-3.5 w-3.5" aria-hidden="true" />
          ) : (
            <ArrowDownNarrowWide className="h-3.5 w-3.5" aria-hidden="true" />
          )}
        </button>
      </div>
    </div>
  );
}
